import Head from "next/head";
import { Link } from "@mui/material";
import { TbShirtSport } from "react-icons/tb";
import { HiOutlineTicket } from "react-icons/hi";
import { MdOutlineEventSeat, MdOutlinedFlag } from "react-icons/md";

import constants from "@/global/config/constants";

import type { TeamConfig } from "@/global/hooks/useTeamConfig";

export const howItWorks = [
  {
    icon: <MdOutlinedFlag />,
    title: "Choose Your Games",
    description: "Select the games you wish to attend.",
  },
  {
    icon: <MdOutlineEventSeat />,
    title: "Login to MLB.com",
    description:
      "Click Continue and connect to your Northwest Arkansas Naturals account using our secure MLB login.",
  },
  {
    icon: <TbShirtSport />,
    title: "Select Your Seats",
    description: "Select the seats you wish to purchase for the games.",
  },
  {
    icon: <HiOutlineTicket />,
    title: "Get Your Tickets",
    description: (
      <>
        Tickets will be available in your Northwest Arkansas Naturals{" "}
        <Link
          target="_blank"
          href="https://www.milb.com/northwest-arkansas/tickets/my-naturals-tickets"
          sx={{
            fontWeight: 500,
            color: "primary.main",
            textDecoration: "none",
          }}
        >
          Account Manager
        </Link>{" "}
        within 24 hours of redemption. Reach out to Live Fan Support for any
        questions.
      </>
    ),
  },
];

export const faqs = [
  {
    title: "How do the Northwest Arkansas Naturals Flex Plan work?",
    description:
      "The Northwest Arkansas Naturals Flex Plan lets you fully customize your experience. You choose any home games that best fit your schedule and select any available seats for each game—giving you flexibility and control over your season.",
  },
  {
    title: "Do I have to select all my games now?",
    description: "Yes, you must select all games at the time of purchase.",
  },
  {
    title: "Can I choose seats throughout the venue?",
    description: "Yes, you can choose from all current available inventory.",
  },
  {
    title: "Where are my tickets delivered?",
    description: (
      <>
        Event tickets are delivered directly within your Northwest Arkansas
        Naturals{" "}
        <Link
          target="_blank"
          href="https://www.milb.com/northwest-arkansas/tickets/my-naturals-tickets"
          sx={{
            fontWeight: 500,
            color: "primary.main",
            textDecoration: "none",
          }}
        >
          Account Manager
        </Link>
        .
      </>
    ),
  },
];

export const AnalyticsHeader = (props: { teamConfig: TeamConfig }) => {
  const { teamSchema } = props.teamConfig;

  return (
    <>
      <Head>
        <script
          nonce={process.env.pixelScriptNonce}
          id={`${teamSchema}-meta-pixel-script`}
          defer
          dangerouslySetInnerHTML={{
            __html: `
              !function(f,b,e,v,n,t,s)
              {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
              n.callMethod.apply(n,arguments):n.queue.push(arguments)};
              if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
              n.queue=[];t=b.createElement(e);t.async=!0;
              t.src=v;s=b.getElementsByTagName(e)[0];
              s.parentNode.insertBefore(t,s)}(window, document,'script',
              'https://connect.facebook.net/en_US/fbevents.js');
              fbq('init', '291157451535721');
              fbq('track', 'PageView');
              console.log('meta pixel page load event fired.');
              window.addEventListener("${constants.customEvents.eventPurchaseSuccess}", function(e) {
                try {
                  const { cartRes, checkoutRes } = e.detail;
                  fbq('track', 'Purchase', {
                    content_name: "${teamSchema}_event_purchase", 
                    value: String(cartRes.allInPrice), 
                    currency: 'USD', 
                    order_id: checkoutRes.orderNumber,
                  });
                  console.log('meta pixel purchase event fired.');
                } catch (error) {
                  console.log('Error in meta pixel purchase event', error); 
                }
              });
            `,
          }}
        />

        <noscript>
          <img
            height="1"
            width="1"
            style={{ display: "none" }}
            src="https://www.facebook.com/tr?id=291157451535721&ev=PageView&noscript=1"
          />
        </noscript>
      </Head>
    </>
  );
};

export const customProgressBarLanguage = [
  {
    selectedEventCount: 0,
    progressBarText: "Add 10 more games to complete your pack.",
  },
  {
    selectedEventCount: 1,
    progressBarText: "Add 9 more games to complete your pack.",
  },
  {
    selectedEventCount: 2,
    progressBarText: "Add 8 more games to complete your pack.",
  },
  {
    selectedEventCount: 3,
    progressBarText: "Add 7 more games to complete your pack.",
  },
  {
    selectedEventCount: 4,
    progressBarText: "Add 6 more games to complete your pack.",
  },
  {
    selectedEventCount: 5,
    progressBarText: "Add 5 more games to complete your pack.",
  },
  {
    selectedEventCount: 6,
    progressBarText: "Add 4 more games to complete your pack.",
  },
  {
    selectedEventCount: 7,
    progressBarText: "Add 3 more games to complete your pack.",
  },
  {
    selectedEventCount: 8,
    progressBarText: "Add 2 more games to complete your pack.",
  },
  {
    selectedEventCount: 9,
    progressBarText: "Add 1 more game to complete your pack.",
  },
  {
    selectedEventCount: 10,
    progressBarText: "You're good to go! Click Continue to view seating options.",
  },
]